import { OnboardingAction } from "pages/Onboardingv2/OnboardingSandbox";
import React, { DragEvent, FC, useState } from "react";
import { createPortal } from "react-dom";
import Scrollbars from "react-custom-scrollbars-2";
import {
  clearInsertNodes,
  setDragAction,
  setIsDragging,
} from "reducers/flow-builder.reducer";
import { useAppDispatch, useAppSelector } from "store/hooks";
import defaultFixtures, { DrawerAction } from "./drawer.fixtures";

export interface FlowBuilderDrawerFixture {
  groupName: string;
  children: {
    id: DrawerAction | OnboardingAction;
    icon: JSX.Element;
    text: string;
    description?: string;
    disabled?: boolean;
    targetId?: string;
  }[];
}

interface FlowBuilderDrawerProps {
  fixtures?: FlowBuilderDrawerFixture[];
  disableStepsCreation?: boolean;
}

const FlowBuilderDrawer: FC<FlowBuilderDrawerProps> = ({ fixtures }) => {
  const fixturesToRender = fixtures || defaultFixtures;

  const { isOnboarding, isDrawerDisabled } = useAppSelector(
    (state) => state.flowBuilder
  );

  const dispatch = useAppDispatch();

  // Tooltip state. The drawer is wrapped in a Scrollbars container with
  // overflow:hidden, so a same-DOM tooltip would get clipped at the right
  // edge. We instead portal the tooltip to document.body and position it
  // via fixed coordinates measured from the help marker's bounding rect.
  const [tooltip, setTooltip] = useState<{
    text: string;
    top: number;
    left: number;
  } | null>(null);

  const showTooltip = (e: React.MouseEvent<HTMLDivElement>, text: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      text,
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
  };
  const hideTooltip = () => setTooltip(null);

  const onDragStart = (
    e: DragEvent<HTMLDivElement>,
    action: DrawerAction | OnboardingAction,
    targetId?: string
  ) => {
    e.dataTransfer.setData("action", action);
    if (targetId) e.dataTransfer.setData("targetId", targetId);
    dispatch(setIsDragging(true));
    dispatch(setDragAction({ type: action }));
  };

  const onDragEnd = () => {
    dispatch(setIsDragging(false));
    dispatch(setDragAction());
    dispatch(clearInsertNodes());
  };

  return (
    <div className="min-w-[232px] w-[232px] border-col border-right-[1px] border-themed-border bg-themed-card h-full">
      {tooltip &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: tooltip.top,
              left: tooltip.left,
              transform: "translateY(-50%)",
            }}
            className="w-[240px] p-[10px] bg-themed-foreground text-themed-background text-[12px] leading-[18px] font-medium rounded shadow-lg z-[10000] pointer-events-none"
          >
            {tooltip.text}
          </div>,
          document.body
        )}
      <Scrollbars>
        <div className="px-[16px] py-5 flex flex-col gap-5">
          {fixturesToRender.map((group) => (
            <div
              className="flex flex-col gap-[10px] w-[200px]"
              key={group.groupName}
            >
              <div className="text-themed-text text-[16px] font-semibold leading-[24px] font-segoe">
                {group.groupName}
              </div>
              {group.children.map((child, i) => (
                <div
                  className={`w-full h-[44px] px-[15px] py-[10px] flex gap-[10px] border border-themed-border  rounded select-none items-center cursor-grab ${
                    (child.disabled || isDrawerDisabled) && !isOnboarding
                      ? "grayscale cursor-not-allowed !text-themed-textSecondary !bg-themed-background"
                      : ""
                  }`}
                  onDragStart={(e) => onDragStart(e, child.id, child.targetId)}
                  onDragEnd={onDragEnd}
                  key={i}
                  id={child.id}
                  draggable={!child.disabled && !isDrawerDisabled}
                >
                  <div className="text-[#6366F1]">{child.icon}</div>
                  <div className="font-segoe font-normal text-[14px] leading-[22px] text-themed-text">
                    {child.text}
                  </div>
                  {child.description && (
                    <div
                      className="ml-auto cursor-help"
                      // Stop drag from starting if the user mousedowns on
                      // the help marker itself — the drawer item is
                      // draggable, but the help marker shouldn't initiate
                      // a drag-to-create gesture.
                      onMouseDown={(e) => e.stopPropagation()}
                      onDragStart={(e) => e.preventDefault()}
                      onMouseEnter={(e) => showTooltip(e, child.description!)}
                      onMouseLeave={hideTooltip}
                    >
                      <div className="w-[16px] h-[16px] rounded-full border border-themed-textSecondary text-themed-textSecondary flex items-center justify-center text-[11px] font-bold leading-none">
                        ?
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </Scrollbars>
    </div>
  );
};

export default FlowBuilderDrawer;
